import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Layers, Calendar, FileText, Shield, Award, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicVerification() {
  const { code } = useParams();
  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    verifyBadge();
  }, [code]);

  const verifyBadge = async () => {
    try {
      const response = await fetch(`${API_URL}/api/verify/${code}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Credential not found');
        } else {
          setError('Verification failed');
        }
        return;
      }

      const data = await response.json();
      setBadge(data);
    } catch (err) {
      setError('Unable to verify credential');
    } finally {
      setLoading(false);
    }
  };

  const getBadgeTitle = (badgeType) => {
    switch (badgeType) {
      case 'strata_badge':
        return 'Strata Record Leadership Credential';
      case 'proof_interview_ready':
        return 'Interview Ready';
      case 'proof_succession_prepared':
        return 'Succession Prepared';
      default:
        return 'Strata Record Credential';
    }
  };

  const getBadgeDescription = (badgeType) => {
    switch (badgeType) {
      case 'strata_badge':
        return 'This individual has built a verified, multi-source leadership record using StrataRecord.com.';
      case 'proof_interview_ready':
        return 'This individual has completed systematic interview preparation using longitudinal leadership data.';
      case 'proof_succession_prepared':
        return 'This individual has completed succession readiness preparation using their leadership record.';
      default:
        return 'A verified Strata Record credential.';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0F14] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-amber-600/30 border-t-amber-600 rounded-full animate-spin mx-auto" />
          <p className="text-slate-400">Verifying credential...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0F14]" data-testid="public-verification-page">
      {/* Header */}
      <header className="border-b border-slate-800/50 bg-[#0D0F14]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/strata-lotus-mark.svg" 
                alt="Strata Record" 
                className="w-8 h-8"
              />
              <span className="font-display text-xl text-white">Strata Record</span>
            </Link>
            <span className="text-sm text-slate-400">Credential Verification</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {error ? (
          /* Error State */
          <Card className="bg-[#1C2130] border-slate-800/50">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="font-display text-2xl text-white mb-3">Verification Failed</h1>
              <p className="text-slate-400 mb-6">{error}</p>
              <p className="text-sm text-slate-500">
                The credential code <span className="font-mono text-slate-300">{code}</span> could not be verified.
              </p>
            </CardContent>
          </Card>
        ) : badge?.verified ? (
          /* Success State */
          <div className="space-y-6">
            {/* Verification Status */}
            <Card className="bg-[#1C2130] border-l-4 border-green-500">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-400" />
                  </div>
                  <div>
                    <p className="text-green-400 text-sm font-medium uppercase tracking-wider">Verified Credential</p>
                    <h1 className="font-display text-2xl text-white">{getBadgeTitle(badge.badge_type)}</h1>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Badge Details */}
            <Card className="bg-[#1C2130] border-slate-800/50">
              <CardContent className="p-8">
                {/* Credential Holder */}
                <div className="text-center mb-8 pb-8 border-b border-slate-800">
                  <img 
                    src="/strata-lotus-mark.svg" 
                    alt="Strata Record Lotus" 
                    className="w-20 h-20 mx-auto mb-4"
                  />
                  <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">Awarded to</p>
                  <h2 className="font-display text-3xl text-white">{badge.user_name}</h2>
                </div>

                {/* Badge Info */}
                <div className="space-y-4">
                  <p className="text-slate-300 text-center leading-relaxed">
                    {getBadgeDescription(badge.badge_type)}
                  </p>

                  <div className="grid grid-cols-2 gap-4 pt-6">
                    {badge.source_count && (
                      <div className="p-4 rounded-lg bg-[#0D0F14] text-center">
                        <FileText className="w-5 h-5 text-amber-500 mx-auto mb-2" />
                        <p className="text-2xl font-display text-white">{badge.source_count}</p>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Sources</p>
                      </div>
                    )}
                    
                    {badge.year_range_start && badge.year_range_end && (
                      <div className="p-4 rounded-lg bg-[#0D0F14] text-center">
                        <Calendar className="w-5 h-5 text-amber-500 mx-auto mb-2" />
                        <p className="text-2xl font-display text-white">
                          {badge.year_range_end - badge.year_range_start + 1}
                        </p>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Year Span</p>
                      </div>
                    )}

                    {badge.role_title && (
                      <div className="p-4 rounded-lg bg-[#0D0F14] text-center col-span-2">
                        <Briefcase className="w-5 h-5 text-amber-500 mx-auto mb-2" />
                        <p className="text-lg font-display text-white">{badge.role_title}</p>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Target Role</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Credential Details */}
                <div className="mt-8 pt-6 border-t border-slate-800 text-center">
                  <p className="text-slate-500 text-xs mb-1">Credential ID</p>
                  <p className="font-mono text-sm text-slate-300">{code}</p>
                  {badge.claimed_at && (
                    <p className="text-slate-500 text-xs mt-3">
                      Issued {new Date(badge.claimed_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* What is Strata Record */}
            <Card className="bg-[#1C2130] border-slate-800/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center shrink-0">
                    <Award className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-white mb-2">What is Strata Record?</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      Strata Record is the world's first portable, multi-observer leadership record platform. 
                      It consolidates 360 feedback, psychometric assessments, performance reviews, and personal reflections 
                      into a unified leadership identity map — owned entirely by the individual.
                    </p>
                    <a 
                      href="https://stratarecord.com"
                      className="inline-flex items-center gap-2 text-amber-500 text-sm mt-3 hover:text-amber-400 transition-colors"
                    >
                      Learn more at StrataRecord.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 mt-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <p className="text-slate-500 text-sm">
              Powered by Strata Record
            </p>
            <p className="text-slate-500 text-sm">
              Verified credential system
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
